// gameengine/gameResources.js

/**
 * Processes daily resource generation from facilities
 * @param {Object} resources - Current resources
 * @param {Array} facilities - Array of base facilities
 * @returns {Object} Updated resources
 */
export const processResourceGeneration = (resources, facilities) => {
  const activeFacilities = facilities.filter(f => 
    (f.is_active === true || f.is_active === 1) && 
    (f.construction_days_remaining === 0 || f.construction_days_remaining === undefined)
  );

  const dailyIncome = calculateDailyIncome(activeFacilities);

  return {
    funds: resources.funds + dailyIncome.funds,
    supplies: resources.supplies + dailyIncome.supplies,
    intel: resources.intel + dailyIncome.intel,
    power: Math.min(resources.power + dailyIncome.power, 100)
  };
};

/**
 * Calculates daily resource income from facilities
 * @param {Array} activeFacilities - Active facilities
 * @returns {Object} Daily income amounts
 */
export const calculateDailyIncome = (activeFacilities) => {
  const income = {
    funds: 0,
    supplies: 5,
    intel: 2,
    power: 0
  };

  activeFacilities.forEach(facility => {
    switch (facility.facility_type) {
      case 'command':
        income.funds += facility.level * 1000;
        break;
      case 'workshop':
        income.supplies += facility.level * 3;
        break;
      case 'intelligence_center':
        income.intel += facility.level * 2;
        break;
      case 'generator':
        income.power += facility.level * 10;
        break;
      case 'research':
      case 'research_lab':
        income.intel += facility.level;
        break;
      case 'trade_depot':
        income.funds += facility.level * 500;
        break;
      default:
        break;
    }
  });

  return income;
};

/**
 * Checks if resources are sufficient for a cost
 * @param {Object} resources - Current resources
 * @param {Object} cost - Required resources
 * @returns {boolean} Whether resources are sufficient
 */
export const canAfford = (resources, cost) => {
  return (
    (resources.funds || 0) >= (cost.funds || 0) &&
    (resources.supplies || 0) >= (cost.supplies || 0) &&
    (resources.intel || 0) >= (cost.intel || 0) &&
    (resources.power || 0) >= (cost.power || 0)
  );
};

/**
 * Deducts resources for a cost
 * @param {Object} resources - Current resources
 * @param {Object} cost - Resources to deduct
 * @returns {Object} Updated resources
 */
export const deductResources = (resources, cost) => {
  return {
    funds: resources.funds - (cost.funds || 0),
    supplies: resources.supplies - (cost.supplies || 0),
    intel: resources.intel - (cost.intel || 0),
    power: resources.power - (cost.power || 0)
  };
};

/**
 * Adds resources
 * @param {Object} resources - Current resources
 * @param {Object} amount - Resources to add
 * @returns {Object} Updated resources
 */
export const addResources = (resources, amount) => {
  return {
    funds: resources.funds + (amount.funds || 0),
    supplies: resources.supplies + (amount.supplies || 0),
    intel: resources.intel + (amount.intel || 0),
    power: Math.min((resources.power || 0) + (amount.power || 0), 100)
  };
};